import useTranslator from "../../hooks/useTranslator";
import useWorkspaceRequirements from "../../hooks/useWorkspaceRequirements";
import { LockedIcon } from "./LockedFeature";
import { Tooltip as ReactTooltip } from "react-tooltip";
import moment from "moment";
import { haveValue } from "../../helper/common";
import { Badge, Button, Icon, getStatusRowClass } from "../design";
import { DOCUMENT_CATEGORIES } from "../../Utils/document-categories";
import constants from "../../Utils/constants";
import {
  getRequiredPayslipPeriods,
  getPayslipDateWarning,
  isPayslipWithinRequiredPeriod,
} from "../../helper/common";
import {
  documentMatchesIdentifiers,
  getTenantDocumentIdentifiers,
} from "../../Utils/tenantDocumentNavigation";
import { PAYSLIP_ORDER } from "../../Utils/payslipRequirement";
import { isCanonicalKeyCovered } from "../../Utils/missingDocNote";
import ViewMissingDocNoteButton, {
  ViewNoteIcon,
} from "./ViewMissingDocNoteButton";
import LandlordNotePillButton, {
  getMissingDocRowAnchorId,
} from "./LandlordNotePillButton";

// True when the tenant covered this missing document with their explanation
// note (TenantMissingDocNote). Matching is canonical-key based so the sign-up,
// profile, and forensic vocabularies all match. The note never marks the
// document as received — it only swaps the missing warning for a "View
// note" button and filters reminder/display counts.
export const isMissingDocKeyExplained = (explainedDocKeys, docKey) => {
  if (!Array.isArray(explainedDocKeys) || explainedDocKeys.length === 0)
    return false;
  return isCanonicalKeyCovered(new Set(explainedDocKeys), docKey);
};

const getUniqueDocKey = (doc) =>
  doc?.documentAnalysisData?.instanceId ??
  doc?.documentAnalysisData?.data?.instanceId ??
  doc?.instanceId ??
  doc?.bucketKey ??
  doc?.bucket_key ??
  doc?.documentAnalysisData?.bucket_key ??
  doc?.documentAnalysisData?.data?.bucket_key ??
  doc?.documentAnalysisData?.bucketName ??
  doc?.bucketName ??
  doc?.id ??
  doc?._id ??
  doc?.document_id ??
  doc?.file_name ??
  `${doc?.type ?? ""}-${doc?.categoryKey ?? ""}-${doc?.created_at ?? ""}`;

const dedupeDocs = (docs = []) => {
  const seen = new Set();
  return docs.filter((doc) => {
    if (!doc) return false;
    const key = getUniqueDocKey(doc);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const dedupeDocRows = (rows = []) => {
  const seen = new Set();
  return rows.filter((row) => {
    const doc = row?.doc;
    if (!doc) return true;
    const key = getUniqueDocKey(doc);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getOwnerBadgeTranslationKey = (doc) =>
  doc?.analysisOwnerType === "legal_entity" ? "thirdPartyDoc" : "othersDocs";

const isScannedReport = (report) =>
  report?.documentType === "is_scanned_document" ||
  report?.isScannedDocument === "yes";

// Which status a document row shows (same order the old icons used).
const getDocStatusKind = (report) => {
  if (isScannedReport(report)) return "scanned";
  if (report?.verdict === "HIGH_RISK") return "highRisk";
  if (report?.verdict === "WARNING") return "warning";
  if (report?.verdict === "NORMAL") return "normal";
  if (report?.verdict === "TRUSTED") return "trusted";
  return "warning";
};

// Rows never get a full coloured border; a line on the left (StatusRow)
// marks them instead. Landlord view: risky or unreadable documents.
// Tenant view: someone else's document (red), which they should remove.
// Forensic results (tags, row colours) are locked when the workspace has not
// switched on document forensics.
const useIsForensicsEnabled = () => {
  const { isEnabled, FEATURES } = useWorkspaceRequirements();
  return isEnabled(FEATURES.DOCUMENT_FORENSICS);
};

const getDocRowTone = ({
  llShowOnly,
  report,
  isOtherUser,
  forensicsEnabled = true,
}) => {
  if (!llShowOnly) return isOtherUser ? "danger" : null;
  if (!forensicsEnabled) return null;
  if (report?.verdict === "HIGH_RISK") return "danger";
  if (isScannedReport(report) || report?.verdict === "WARNING") {
    return "warning";
  }
  return null;
};

// Icon and colour per document status; the filter chips in DetailsDocs use
// the same table so a chip always matches its rows.
export const STATUS_ICONS = {
  trusted: { icon: "shieldCheck", tone: "success" },
  normal: { icon: "check", tone: "success" },
  warning: { icon: "warning", tone: "warning" },
  scanned: { icon: "info", tone: "warning" },
  highRisk: { icon: "warning", tone: "danger" },
  missing: { icon: "minus", tone: "muted" },
};

// Round status icon at the start of a document row.
// The status comes from document forensics, so it is a lock when the workspace
// has not switched forensics on. ("missing" is not a forensic result.)
function DocStatusIcon({ kind, tooltipId }) {
  const isForensicsEnabled = useIsForensicsEnabled();
  if (kind !== "missing" && !isForensicsEnabled) {
    return <LockedIcon className="details-docs__status" />;
  }
  const { icon, tone } = STATUS_ICONS[kind] ?? STATUS_ICONS.warning;
  return (
    <span
      className={`details-docs__status details-docs__status--${tone}`}
      data-tooltip-id={tooltipId}
    >
      <Icon name={icon} size={16} />
    </span>
  );
}

// Yellow chip: this document belongs to someone else.
// Company login tenants: an authorised signatory's ID (their KYB KYC) gets a
// neutral chip with the signatory's name instead.
function DocOwnerBadge({ doc, getDocOwnerLabel, _t }) {
  if (doc?.signatory_name) {
    return (
      <Badge tone="neutral" className="details-docs__owner">
        <Icon name="idCard" size={12} />
        <span className="details-docs__owner-text" title={doc.signatory_name}>
          {_t(
            "authorisedSignatoryKycDocument",
            "Company documents: chip on an authorised signatory's ID document from their KYB KYC, followed by their name"
          )}
          : {doc.signatory_name}
        </span>
      </Badge>
    );
  }
  const label = getDocOwnerLabel(doc);
  return (
    <Badge tone="warning" className="details-docs__owner">
      <Icon name="user" size={12} />
      <span className="details-docs__owner-text" title={`${label}`}>
        {_t(getOwnerBadgeTranslationKey(doc))}: {label}
      </span>
    </Badge>
  );
}

// Delete (other people's docs, tenant view) and view buttons on a row.
function DocRowActions({
  doc,
  llShowOnly,
  readOnly,
  isOtherUserDoc,
  setDeleteDocumentConfirmation,
  setselectedDocument,
  handleViewDocument,
  showMissingText = false,
  _t,
}) {
  return (
    <div className="details-docs__row-actions">
      {isOtherUserDoc(doc) && !llShowOnly && !readOnly && (
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          iconStart={<Icon name="trash" size={18} />}
          aria-label={_t("delete", "Delete")}
          onClick={() => {
            setDeleteDocumentConfirmation(true);
            setselectedDocument(doc);
          }}
        />
      )}
      {doc ? (
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          iconStart={<Icon name="eye" size={18} />}
          aria-label={_t("viewDetails", "View details")}
          onClick={(e) => handleViewDocument(e, doc)}
        />
      ) : (
        showMissingText && (
          <span className="details-docs__row-note">
            {_t("documentMissing")}
          </span>
        )
      )}
    </div>
  );
}

export function MissingDocsTag({ llShowOnly, tooltipIndex }) {
  const { _t } = useTranslator();

  return (
    <span
      className="details-docs__flag"
      data-tooltip-id={
        llShowOnly ? `missing-doc-icon-${tooltipIndex}` : undefined
      }
    >
      <Badge tone="warning">{_t("missingDocs")}</Badge>
    </span>
  );
}

// Warning / scanned / missing marks on the right of a category header.
function CategoryFlags({
  llShowOnly,
  hasOthersDocument = false,
  ownerKey = "othersDocs",
  isScannedDoc = false,
  hasWarning = false,
  hasHighRisk = false,
  hasMissing = false,
  tooltipIndex,
  missingTooltipIndex = tooltipIndex,
  _t,
}) {
  // Scanned / warning / high-risk marks are forensic results: one lock instead
  // when the workspace has not switched on document forensics
  const isForensicsEnabled = useIsForensicsEnabled();
  const hasForensicFlag = isScannedDoc || hasWarning || hasHighRisk;
  return (
    <>
      {llShowOnly && !isForensicsEnabled && hasForensicFlag && (
        <LockedIcon className="details-docs__flag" />
      )}
      {!llShowOnly && hasOthersDocument && (
        <Badge tone="warning">
          <Icon name="user" size={12} />
          {_t(ownerKey)}
        </Badge>
      )}
      {llShowOnly && isForensicsEnabled && isScannedDoc && (
        <span
          className="details-docs__flag details-docs__flag--warning"
          data-tooltip-id={`scanned-doc-icon-${tooltipIndex}`}
        >
          <Icon name="info" size={16} />
        </span>
      )}
      {hasMissing && (
        <MissingDocsTag
          llShowOnly={llShowOnly}
          tooltipIndex={missingTooltipIndex}
        />
      )}
      {llShowOnly && isForensicsEnabled && hasWarning && (
        <span
          className="details-docs__flag details-docs__flag--warning"
          data-tooltip-id={`warning-doc-icon-${tooltipIndex}`}
        >
          <Icon name="warning" size={16} />
        </span>
      )}
      {llShowOnly && isForensicsEnabled && hasHighRisk && (
        <span
          className="details-docs__flag details-docs__flag--danger"
          data-tooltip-id={`high-risk-doc-icon-${tooltipIndex}`}
        >
          <Icon name="warning" size={16} />
        </span>
      )}
    </>
  );
}

// Clickable title row of a category (Identification, Income, ...).
function CategoryHeader({ icon, title, open, onToggle, children }) {
  return (
    <div
      className="details-docs__cat-head"
      role="button"
      tabIndex={0}
      aria-expanded={!!open}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <div className="details-docs__cat-title">
        <span className="details-docs__cat-icon">
          <Icon name={icon} size={20} />
        </span>
        <span>{title}</span>
      </div>
      <div className="details-docs__cat-meta">
        {children}
        <Icon
          name="chevronDown"
          size={18}
          className={`details-docs__cat-chevron ${
            open ? "details-docs__chevron--open" : ""
          }`.trim()}
        />
      </div>
    </div>
  );
}

// "Documents: 3/3" in a category header.
function CategoryCount({ value, _t }) {
  return (
    <span className="details-docs__cat-count">
      {_t("documents")}: {value}
    </span>
  );
}

// Display-only note indicator in a category header (landlord view only).
// Shown before the documents count when the section contains rows with a
// landlord "View note" pill. The number is the count of those rows.
function CategoryNoteCount({ count }) {
  if (!count || count <= 0) return null;
  return (
    <span
      className="details-docs__cat-note-count"
      aria-label={`View note: ${count}`}
      title="View note"
    >
      <ViewNoteIcon />
      <span>{count}</span>
    </span>
  );
}

// Tooltip Components
export const ScannedTooltip = ({ index }) => {
  const { _t } = useTranslator();
  return (
    <ReactTooltip
      id={`scanned-doc-icon-${index}`}
      noArrow
      className="react-tooltip-styling details-skor-tooltip"
      render={() => (
        <p>{_t("thisSectionContainsScannedDocumentsThatCannotBeAnalyzed")}</p>
      )}
    />
  );
};

export const MissingDocTooltip = ({ index }) => {
  const { _t } = useTranslator();
  return (
    <ReactTooltip
      id={`missing-doc-icon-${index}`}
      noArrow
      className="react-tooltip-styling details-skor-tooltip"
      render={() => <p>{_t("missingDocTooltipDescription")}</p>}
    />
  );
};

export const WarningTooltip = ({ index }) => {
  const { _t } = useTranslator();
  return (
    <ReactTooltip
      id={`warning-doc-icon-${index}`}
      noArrow
      className="react-tooltip-styling details-skor-tooltip"
      render={() => <p>{_t("someDocumentsInThisSectionRequireReview")}</p>}
    />
  );
};

export const HighRiskTooltip = ({ index }) => {
  const { _t } = useTranslator();
  return (
    <ReactTooltip
      id={`high-risk-doc-icon-${index}`}
      noArrow
      className="react-tooltip-styling details-skor-tooltip"
      render={() => (
        <p>{_t("someDocumentsInThisSectionShowSignsOfTampering")}</p>
      )}
    />
  );
};

// NoDocumentsFound - Renders the "No document found" message when no sections have content
export function NoDocumentsFound({
  forensic_docs_map,
  employmentType,
  getDocsForType,
  getPayslipDocs,
  getRequiredProtectedHousingDocs,
  getOtherDocs,
  selectedVerdicts,
  matchesSelectedVerdicts,
}) {
  const { _t } = useTranslator();

  // Check if identification section would render
  const hasIdentification =
    (forensic_docs_map?.[employmentType]?.identification?.length ?? 0) > 0 &&
    (() => {
      const allDocs = forensic_docs_map[employmentType].identification;
      const availableDocs = dedupeDocs(
        allDocs.flatMap((item) =>
          getDocsForType(item, [DOCUMENT_CATEGORIES.IDENTIFICATION])
        )
      )
        .filter(Boolean)
        .filter((doc) => matchesSelectedVerdicts(doc));
      return !(selectedVerdicts.length > 0 && availableDocs.length === 0);
    })();

  const getCombinedDocs = (section) => {
    const compulsory = section?.compulsory ?? [];
    const additional = section?.additional ?? [];
    return [...compulsory, ...additional];
  };

  // Check if employment status section would render
  const hasEmploymentStatus =
    getCombinedDocs(forensic_docs_map?.[employmentType]?.employment_status)
      .length > 0 &&
    (() => {
      const allDocs = getCombinedDocs(
        forensic_docs_map[employmentType]?.employment_status
      );
      const filteredDocs = dedupeDocs(
        allDocs.flatMap((item) =>
          getDocsForType(item, [DOCUMENT_CATEGORIES.EMPLOYMENT_STATUS])
        )
      ).filter((doc) => matchesSelectedVerdicts(doc));
      return !(selectedVerdicts.length > 0 && filteredDocs.length === 0);
    })();

  // Check if income taxes section would render
  const hasIncomeTaxes =
    getCombinedDocs(forensic_docs_map?.[employmentType]?.income_taxes).length >
      0 &&
    (() => {
      const allDocs = getCombinedDocs(
        forensic_docs_map[employmentType]?.income_taxes
      );
      const filteredAllDocs = dedupeDocs(
        allDocs.flatMap((item) =>
          item?.toLowerCase()?.includes("payslip")
            ? getPayslipDocs()
            : getDocsForType(item, [
                DOCUMENT_CATEGORIES.TAX,
                DOCUMENT_CATEGORIES.INCOME,
                DOCUMENT_CATEGORIES.PAYMENT_RECEIPT,
                DOCUMENT_CATEGORIES.FINANCIAL,
              ])
        )
      ).filter((doc) => matchesSelectedVerdicts(doc));

      return !(selectedVerdicts.length > 0 && filteredAllDocs.length === 0);
    })();

  const hasProtectedHousing = (() => {
    const allDocs = getRequiredProtectedHousingDocs();

    if (allDocs.length === 0) return false;

    const filteredDocs = allDocs
      .flatMap((item) => getDocsForType(item))
      .filter((doc) => matchesSelectedVerdicts(doc));

    return !(selectedVerdicts.length > 0 && filteredDocs.length === 0);
  })();

  // Check if other documents section would render
  const hasOtherDocs = getOtherDocs?.length > 0;

  const noSectionsRendered =
    !hasIdentification &&
    !hasEmploymentStatus &&
    !hasIncomeTaxes &&
    !hasProtectedHousing &&
    !hasOtherDocs &&
    forensic_docs_map !== undefined;

  return noSectionsRendered ? (
    <div className="noDocFound">
      <div>
        <img src={"/images/no-document-image.svg"} alt="" />
      </div>
      <div>{_t("noDocumentsFound")}</div>
    </div>
  ) : null;
}

// DocumentRow - Reusable component for rendering a single document row
// Service-file tags shown on profile documents (TenantServiceFile.service).
// A document can carry multiple tags when several service files point to it.
export const SERVICE_TAG_I18N_KEYS = {
  KYC: "usedForKyc",
  EXPERIAN: "usedForDefaults",
  BANK_SCORE: "usedForBankScore",
};

export function DocServiceTags({ tags = [] }) {
  const { _t } = useTranslator();
  const keys = (Array.isArray(tags) ? tags : [])
    .map(
      (service) =>
        SERVICE_TAG_I18N_KEYS[
          String(service ?? "")
            .toUpperCase()
            .trim()
        ]
    )
    .filter(Boolean);
  if (keys.length === 0) return null;
  return (
    <span className="doc-service-tags">
      {keys.map((key) => (
        <span key={key} className="doc-service-tag">
          {/* <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1 1 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
            <path d="m9 12 2 2 4-4" />
          </svg> */}
          {_t(key)}
        </span>
      ))}
    </span>
  );
}

export function DocumentRow({
  doc,
  index,
  llShowOnly,
  readOnly = false,
  forensicDocumentReport,
  currentLang,
  isOtherUserDoc,
  getDocOwnerLabel,
  getDisplayDocName,
  forensicAnalysis,
  setDeleteDocumentConfirmation,
  setselectedDocument,
  handleViewDocument,
  verdictPriority,
  _t,
  tooltipIndex = 0,
  getDocServiceTags = () => [],
  highlightedDocumentIdentifiers = [],
}) {
  // The forensic tag (trusted, warning, ...) is locked when the workspace has
  // not switched on document forensics
  const isForensicsEnabled = useIsForensicsEnabled();

  const getStatusKind = () => {
    if (!doc) return "missing";

    if (
      doc?.type === "Declaration Consent" ||
      doc?.type === "Client Identification Form"
    ) {
      return "normal";
    }

    return getDocStatusKind(forensicDocumentReport?.[doc?.file_name]);
  };

  const renderTooltip = () => {
    if (!llShowOnly) return null;

    const report = forensicDocumentReport?.[doc?.file_name];
    const hasReportData =
      haveValue(report?.explanationSummary?.["en"]) ||
      haveValue(report?.recommendedAction?.["en"]) ||
      haveValue(report?.score);

    if (!hasReportData && doc?.type !== "Declaration Consent") {
      return (
        <ReactTooltip
          id={`doc-tooltip-${tooltipIndex}-${doc?._id}`}
          noArrow
          className="react-tooltip-styling details-skor-tooltip"
          render={() => (
            <p>
              <span>{_t("explanationSummary")}: </span>
              {_t(
                "notAbleToRunForensicCheckForThisDocument",
                "Fall back text in document section tooltip when for a document forensic was not able to run.(Shown on hover)"
              )}
            </p>
          )}
        />
      );
    }

    const lang = currentLang === "en-us" ? "en" : "es";
    const isScanned =
      report?.documentType === "is_scanned_document" ||
      report?.isScannedDocument === "yes";

    return (
      <ReactTooltip
        id={`doc-tooltip-${tooltipIndex}-${doc?._id}`}
        noArrow
        className="react-tooltip-styling details-skor-tooltip"
        render={() => (
          <div>
            {report?.explanationSummary?.[lang] && (
              <>
                <p>
                  <span>{_t("explanationSummary")}: </span>
                  {isScanned
                    ? _t("thisDocumentIsAScanSoCouldNotRunForensicAnalysis")
                    : report?.explanationSummary?.[lang]}
                </p>
                <hr className="document-dropdown-tooltip-hr" />
              </>
            )}
            {report?.recommendedAction?.[lang] && (
              <p>
                <span>{_t("recommendedAction")}: </span>
                {isScanned
                  ? _t(
                      "toEnableForensicChecksAskTheTenantToUploadTheOriginalDigitalPdf"
                    )
                  : report?.recommendedAction?.[lang]}
              </p>
            )}
            {!isScanned && report?.score && (
              <p>
                <span>{_t("confidenceInAuthenticity")}: </span>
                {`${report.score}%`}
              </p>
            )}
          </div>
        )}
      />
    );
  };

  return (
    <div
      key={doc?._id || index}
      className={`details-docs__row ${getStatusRowClass(
        getDocRowTone({
          llShowOnly,
          report: forensicDocumentReport?.[doc?.file_name],
          isOtherUser: isOtherUserDoc(doc),
          forensicsEnabled: isForensicsEnabled,
        })
      )} ${
        documentMatchesIdentifiers(doc, highlightedDocumentIdentifiers)
          ? "tenant-document-navigation-highlight"
          : ""
      }`}
      data-tenant-document-identifiers={JSON.stringify(
        getTenantDocumentIdentifiers(doc)
      )}
      style={{
        opacity: !doc ? 0.4 : 1,
        pointerEvents: !doc ? "none" : "",
      }}
    >
      <div className="details-docs__row-main">
        {llShowOnly && (
          <>
            <DocStatusIcon
              kind={getStatusKind()}
              tooltipId={`doc-tooltip-${tooltipIndex}-${doc?._id}`}
            />
            {isForensicsEnabled && renderTooltip()}
          </>
        )}
        <div>
          <p className="details-docs__name">
            {getDisplayDocName(doc)}
            <DocServiceTags tags={getDocServiceTags(doc)} />
            {doc?.type === "Working life certificate" &&
              haveValue(
                forensicAnalysis?.["Work Life Certificate"]?.dataInJSON
                  ?.reportDate
              ) && (
                <>
                  {" "}
                  (
                  {moment(
                    forensicAnalysis?.["Work Life Certificate"]?.dataInJSON
                      ?.reportDate
                  ).format("D MMMM YYYY")}
                  )
                </>
              )}
          </p>
          {doc && (
            <p className="details-docs__meta">
              <span className="details-docs__date">
                {_t("uploadedOn")}{" "}
                {moment(doc?.created_at).format("DD-MM-YYYY")}
              </span>
              {(isOtherUserDoc(doc) || doc?.signatory_name) && (
                <DocOwnerBadge
                  doc={doc}
                  getDocOwnerLabel={getDocOwnerLabel}
                  _t={_t}
                />
              )}
            </p>
          )}
        </div>
      </div>
      <DocRowActions
        doc={doc}
        llShowOnly={llShowOnly}
        readOnly={readOnly}
        isOtherUserDoc={isOtherUserDoc}
        setDeleteDocumentConfirmation={setDeleteDocumentConfirmation}
        setselectedDocument={setselectedDocument}
        handleViewDocument={handleViewDocument}
        _t={_t}
      />
    </div>
  );
}

// OtherDocsSection - Renders the "Other documents" section
export function OtherDocsSection({
  otherDocs,
  llShowOnly,
  readOnly = false,
  handleDropdownToggle,
  showDropdown,
  forensicDocumentReport,
  documentsByType,
  currentLang,
  isOtherUserDoc,
  getDocOwnerLabel,
  getDisplayDocName,
  setDeleteDocumentConfirmation,
  setselectedDocument,
  handleViewDocument,
  verdictPriority,
  forensicAnalysis,
  getDocServiceTags = () => [],
  highlightedDocumentIdentifiers = [],
  showClientIdentificationMissingDoc = false,
}) {
  const { _t } = useTranslator();
  showClientIdentificationMissingDoc =
    showClientIdentificationMissingDoc &&
    !(otherDocs ?? []).some(
      (doc) => doc?.type === "Client Identification Form"
    );
  if (!otherDocs?.length && !showClientIdentificationMissingDoc) return null;

  let hasHighRisk = false;
  let hasWarning = false;
  let hasOthersDocument = false;
  let hasThirdPartyDocument = false;
  let isScannedDoc = false;

  for (const item of otherDocs) {
    if (item?.type === "Client Identification Form") continue;
    const fileName = documentsByType?.[item?.type]?.file_name;
    let verdict =
      item?.type === "Declaration Consent"
        ? "NORMAL"
        : forensicDocumentReport?.[fileName]?.verdict;
    const isScanned =
      item?.type === "Declaration Consent"
        ? false
        : forensicDocumentReport?.[fileName]?.documentType ===
            "is_scanned_document" ||
          forensicDocumentReport?.[fileName]?.isScannedDocument === "yes";
    if (isScanned) {
      isScannedDoc = true;
      continue;
    }
    if (verdict === "HIGH_RISK") hasHighRisk = true;
    if (verdict === "WARNING") hasWarning = true;
  }

  for (const doc of otherDocs) {
    if (isOtherUserDoc(doc)) {
      hasOthersDocument = true;
      if (doc?.analysisOwnerType === "legal_entity") {
        hasThirdPartyDocument = true;
        break;
      }
    }
  }

  const getPriority = (doc) => {
    if (!doc) return -1;
    if (doc?.type === "Declaration Consent")
      return verdictPriority["NORMAL"] ?? 3;
    if (doc?.type === "Client Identification Form")
      return verdictPriority["NORMAL"] ?? 3;
    const isScanned =
      forensicDocumentReport?.[doc?.file_name]?.documentType ===
        "is_scanned_document" ||
      forensicDocumentReport?.[doc?.file_name]?.isScannedDocument === "yes";
    if (isScanned) return verdictPriority["SCANNED"];
    const verdict = forensicDocumentReport?.[doc?.file_name]?.verdict;
    if (!verdict) return verdictPriority["WARNING"];
    return verdictPriority[verdict] ?? 3;
  };

  const sortedDocs = [...otherDocs].sort(
    (a, b) => getPriority(a) - getPriority(b)
  );

  return (
    <div className="details-docs__cat">
      <CategoryHeader
        icon="fileText"
        title={_t("otherDocuments")}
        open={showDropdown.fourth}
        onToggle={() => handleDropdownToggle("fourth")}
      >
        <CategoryFlags
          llShowOnly={llShowOnly}
          hasOthersDocument={hasOthersDocument}
          ownerKey={hasThirdPartyDocument ? "thirdPartyDoc" : "othersDocs"}
          isScannedDoc={isScannedDoc}
          hasWarning={hasWarning}
          hasHighRisk={hasHighRisk}
          hasMissing={showClientIdentificationMissingDoc}
          tooltipIndex={4}
          _t={_t}
        />
        <ScannedTooltip index={4} />
        <WarningTooltip index={4} />
        <HighRiskTooltip index={4} />

        <CategoryCount
          value={
            showClientIdentificationMissingDoc
              ? `${otherDocs?.length ?? 0}/${(otherDocs?.length ?? 0) + 1}`
              : otherDocs?.length
          }
          _t={_t}
        />
      </CategoryHeader>
      {showDropdown.fourth && (
        <div className="details-docs__rows">
          {showClientIdentificationMissingDoc && (
            <div className="details-docs__row">
              <div className="details-docs__row-main">
                {llShowOnly && <DocStatusIcon kind="missing" />}
                <div>
                  <p className="details-docs__name" style={{ opacity: 0.4 }}>
                    {_t("viviaClientIdentificationForm")}
                  </p>
                  <p className="details-docs__meta">
                    <span className="details-docs__date">
                      {_t("viviaFormNotSignedYet")}
                    </span>
                  </p>
                </div>
              </div>
              <div className="details-docs__row-note" style={{ opacity: 0.4 }}>
                {_t("documentMissing")}
              </div>
            </div>
          )}
          {sortedDocs.map((doc, index) => (
            <DocumentRow
              key={doc?._id || index}
              doc={doc}
              index={index}
              llShowOnly={llShowOnly}
              readOnly={readOnly}
              forensicDocumentReport={forensicDocumentReport}
              currentLang={currentLang}
              isOtherUserDoc={isOtherUserDoc}
              getDocOwnerLabel={getDocOwnerLabel}
              getDisplayDocName={getDisplayDocName}
              forensicAnalysis={forensicAnalysis}
              setDeleteDocumentConfirmation={setDeleteDocumentConfirmation}
              setselectedDocument={setselectedDocument}
              handleViewDocument={handleViewDocument}
              verdictPriority={verdictPriority}
              _t={_t}
              tooltipIndex={4}
              getDocServiceTags={getDocServiceTags}
              highlightedDocumentIdentifiers={highlightedDocumentIdentifiers}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ProtectedHousingSection - Renders the "Protected Housing" section
// This section can have missing documents (doc can be null)
export function ProtectedHousingSection({
  llShowOnly,
  readOnly = false,
  handleDropdownToggle,
  showDropdown,
  forensicDocumentReport,
  currentLang,
  isOtherUserDoc,
  getDocOwnerLabel,
  getDisplayDocName,
  setDeleteDocumentConfirmation,
  setselectedDocument,
  handleViewDocument,
  verdictPriority,
  // Function-based props
  getRequiredProtectedHousingDocs,
  getDocsForType,
  selectedVerdicts,
  matchesSelectedVerdicts,
  getDocServiceTags = () => [],
  highlightedDocumentIdentifiers = [],
  // Tenant explanation note (TenantMissingDocNote): canonical keys of the
  // explained documents plus the callback opening the explanation modal.
  // The callback is only passed on the tenant's own profile view.
  // onScrollToLandlordNote is the landlord counterpart: scrolling to the
  // read-only note box instead of opening the modal. Only passed on
  // landlord views.
  explainedDocKeys = [],
  onViewMissingDocNote = null,
  onScrollToLandlordNote = null,
}) {
  const isForensicsEnabled = useIsForensicsEnabled();
  const { _t } = useTranslator();
  const allDocs = getRequiredProtectedHousingDocs();

  if (allDocs.length === 0) return null;

  const protectedRows = allDocs.flatMap((item) => {
    const docs = getDocsForType(item);
    return docs.length
      ? docs.map((doc) => ({ item, doc }))
      : [{ item, doc: null }];
  });

  const filteredDocs =
    selectedVerdicts.length === 0
      ? protectedRows
      : protectedRows.filter(({ doc }) => doc && matchesSelectedVerdicts(doc));

  if (selectedVerdicts.length > 0 && filteredDocs.length === 0) return null;

  const totalDocs = filteredDocs.length;
  const uploadedDocs = filteredDocs.filter(({ doc }) => !!doc).length;

  let hasHighRisk = false;
  let hasWarning = false;
  let hasOthersDocument = false;
  let isScannedDoc = false;

  for (const { doc } of filteredDocs) {
    const fileName = doc?.file_name;
    const verdict = forensicDocumentReport?.[fileName]?.verdict;

    const isScanned =
      forensicDocumentReport?.[fileName]?.documentType ===
        "is_scanned_document" ||
      forensicDocumentReport?.[fileName]?.isScannedDocument === "yes";
    if (isScanned) {
      isScannedDoc = true;
      continue;
    }
    if (verdict === "HIGH_RISK") hasHighRisk = true;
    if (verdict === "WARNING") hasWarning = true;
  }

  const hasMissingDocs = uploadedDocs < totalDocs;

  for (const { doc } of filteredDocs) {
    if (isOtherUserDoc(doc)) {
      hasOthersDocument = true;
      break;
    }
  }

  const getPriority = (doc) => {
    if (!doc) return -1;
    const isScanned =
      forensicDocumentReport?.[doc?.file_name]?.documentType ===
        "is_scanned_document" ||
      forensicDocumentReport?.[doc?.file_name]?.isScannedDocument === "yes";
    if (isScanned) return verdictPriority["SCANNED"];
    const verdict = forensicDocumentReport?.[doc?.file_name]?.verdict;
    if (!verdict) return verdictPriority["WARNING"];
    return verdictPriority[verdict] ?? 3;
  };

  const sortedDocs = [...filteredDocs].sort((a, b) => {
    const docA = a?.doc;
    const docB = b?.doc;
    return getPriority(docA) - getPriority(docB);
  });

  // Landlord header indicator: number of rows in this section showing the
  // read-only "View note" pill (display only, not clickable).
  const protectedNotePillCount =
    typeof onScrollToLandlordNote === "function"
      ? filteredDocs.filter(
          ({ item, doc }) =>
            !doc && isMissingDocKeyExplained(explainedDocKeys, item)
        ).length
      : 0;

  return (
    <div className="details-docs__cat">
      <CategoryHeader
        icon="home"
        title={_t("protectedHousing")}
        open={showDropdown.protectedHousing}
        onToggle={() => handleDropdownToggle("protectedHousing")}
      >
        <CategoryFlags
          llShowOnly={llShowOnly}
          hasOthersDocument={hasOthersDocument}
          isScannedDoc={isScannedDoc}
          hasWarning={hasWarning}
          hasHighRisk={hasHighRisk}
          hasMissing={filteredDocs.some(
            ({ item, doc }) =>
              !doc && !isMissingDocKeyExplained(explainedDocKeys, item)
          )}
          tooltipIndex={5}
          _t={_t}
        />
        <ScannedTooltip index={5} />
        <MissingDocTooltip index={5} />
        <WarningTooltip index={5} />
        <HighRiskTooltip index={5} />

        <CategoryNoteCount count={protectedNotePillCount} />
        <CategoryCount value={`${uploadedDocs}/${totalDocs}`} _t={_t} />
      </CategoryHeader>

      {showDropdown.protectedHousing && (
        <div className="details-docs__rows">
          {sortedDocs.map(({ item, doc }) => {
            const title = getDisplayDocName(doc) || _t(item, "", "fixed");
            // Explained documents keep their missing state, but the tenant
            // sees a "View note" button instead of the missing warning.
            const rowShowsNoteButton =
              !doc &&
              typeof onViewMissingDocNote === "function" &&
              isMissingDocKeyExplained(explainedDocKeys, item);
            // A document uploaded for another person does not count for this
            // tenant, so an explained key still earns a "View note" button
            // on its row (next to the third-party badge).
            const thirdPartyShowsNoteButton =
              doc &&
              isOtherUserDoc(doc) &&
              typeof onViewMissingDocNote === "function" &&
              isMissingDocKeyExplained(explainedDocKeys, item);
            // Landlord: explained missing rows show a note pill scrolling to
            // the read-only note box. Only when the document is actually
            // missing — never on uploaded rows, including third-party ones.
            const protectedShowsNotePill =
              !doc &&
              typeof onScrollToLandlordNote === "function" &&
              isMissingDocKeyExplained(explainedDocKeys, item);

            return (
              <div
                key={doc?.file_name ?? `${item}-missing`}
                id={
                  protectedShowsNotePill
                    ? getMissingDocRowAnchorId(item)
                    : undefined
                }
                className={`details-docs__row ${getStatusRowClass(
                  getDocRowTone({
                    llShowOnly,
                    report: forensicDocumentReport?.[doc?.file_name],
                    forensicsEnabled: isForensicsEnabled,
                    isOtherUser: isOtherUserDoc(doc),
                  })
                )} ${
                  documentMatchesIdentifiers(
                    doc,
                    highlightedDocumentIdentifiers
                  )
                    ? "tenant-document-navigation-highlight"
                    : ""
                }`}
                data-tenant-document-identifiers={JSON.stringify(
                  getTenantDocumentIdentifiers(doc)
                )}
                style={{
                  opacity:
                    !doc && !rowShowsNoteButton && !protectedShowsNotePill
                      ? 0.4
                      : 1,
                  pointerEvents:
                    !doc && !rowShowsNoteButton && !protectedShowsNotePill
                      ? "none"
                      : "",
                }}
              >
                <div className="details-docs__row-main">
                  {llShowOnly && (
                    <DocStatusIcon
                      kind={
                        doc
                          ? getDocStatusKind(
                              forensicDocumentReport?.[doc?.file_name]
                            )
                          : "missing"
                      }
                      tooltipId={`doc-fifth-dropdown-${doc?._id || item}`}
                    />
                  )}
                  <div>
                    <p className="details-docs__name">
                      {title}
                      <DocServiceTags tags={getDocServiceTags(doc)} />
                    </p>
                    <p className="details-docs__meta">
                      <span className="details-docs__date">
                        {doc ? (
                          <>
                            {_t("uploadedOn")}{" "}
                            {moment(doc?.created_at).format("DD-MM-YYYY")}
                          </>
                        ) : rowShowsNoteButton ? null : (
                          _t("documentMissing")
                        )}
                      </span>
                      {(isOtherUserDoc(doc) || doc?.signatory_name) && (
                        <DocOwnerBadge
                          doc={doc}
                          getDocOwnerLabel={getDocOwnerLabel}
                          _t={_t}
                        />
                      )}
                    </p>
                  </div>
                </div>
                <DocRowActions
                  doc={doc}
                  llShowOnly={llShowOnly}
                  readOnly={readOnly}
                  isOtherUserDoc={isOtherUserDoc}
                  setDeleteDocumentConfirmation={setDeleteDocumentConfirmation}
                  setselectedDocument={setselectedDocument}
                  handleViewDocument={handleViewDocument}
                  showMissingText={
                    !rowShowsNoteButton && !thirdPartyShowsNoteButton
                  }
                  _t={_t}
                />
                {(rowShowsNoteButton || thirdPartyShowsNoteButton) && (
                  <ViewMissingDocNoteButton onViewNote={onViewMissingDocNote} />
                )}
                {protectedShowsNotePill && (
                  <LandlordNotePillButton
                    onScrollToNote={onScrollToLandlordNote}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// IdentificationSection - Renders the "Identification" section
export function IdentificationSection({
  llShowOnly,
  readOnly = false,
  handleDropdownToggle,
  showDropdown,
  forensicDocumentReport,
  currentLang,
  isOtherUserDoc,
  getDocOwnerLabel,
  getDisplayDocName,
  setDeleteDocumentConfirmation,
  setselectedDocument,
  handleViewDocument,
  verdictPriority,
  forensicAnalysis,
  // Function-based props
  forensicDocsMap,
  employmentType,
  getDocsForType,
  selectedVerdicts,
  matchesSelectedVerdicts,
  showKycCustomTooltipMessages,
  getDocServiceTags = () => [],
  highlightedDocumentIdentifiers = [],
}) {
  const isForensicsEnabled = useIsForensicsEnabled();
  const { _t } = useTranslator();
  const allDocs = forensicDocsMap?.[employmentType]?.identification ?? [];

  if (allDocs.length === 0) return null;

  let availableDocs = allDocs
    .flatMap((item) =>
      getDocsForType(item, [DOCUMENT_CATEGORIES.IDENTIFICATION])
    )
    .filter(Boolean);

  availableDocs = dedupeDocs(availableDocs)
    .filter(Boolean)
    .filter((doc) => matchesSelectedVerdicts(doc));

  // Removing section if there is no document in filter found
  if (selectedVerdicts.length > 0 && availableDocs.length === 0) return null;

  const getDocPriority = (doc) => {
    const isScanned =
      forensicDocumentReport?.[doc?.file_name]?.documentType ===
        "is_scanned_document" ||
      forensicDocumentReport?.[doc?.file_name]?.isScannedDocument === "yes";

    if (isScanned) return verdictPriority["SCANNED"];

    const verdict = forensicDocumentReport?.[doc?.file_name]?.verdict;

    if (!verdict) return verdictPriority["WARNING"];

    return verdictPriority[verdict] ?? 3;
  };

  availableDocs = [...availableDocs].sort(
    (a, b) => getDocPriority(a) - getDocPriority(b)
  );

  const totalDocs = availableDocs.length;
  const uploadedDocs = availableDocs.length;

  // 🔹 Detect verdicts present in this section
  let hasHighRisk = false;
  let hasWarning = false;
  let hasOthersDocument = false;
  let isScannedDoc = false;

  // detecting high risk and warning tag
  for (const doc of availableDocs) {
    const fileName = doc?.file_name;
    const verdict = forensicDocumentReport?.[fileName]?.verdict;
    const isScanned =
      forensicDocumentReport?.[fileName]?.documentType ===
        "is_scanned_document" ||
      forensicDocumentReport?.[fileName]?.isScannedDocument === "yes";
    if (isScanned) {
      isScannedDoc = true;
      continue;
    }
    if (verdict === "HIGH_RISK") hasHighRisk = true;
    if (verdict === "WARNING") hasWarning = true;
  }

  // detecting others document for tag
  for (const doc of availableDocs) {
    if (isOtherUserDoc(doc)) {
      hasOthersDocument = true;
      break;
    }
  }

  return (
    <div className="">
      <div className="details-docs__cat">
        <CategoryHeader
          icon="idCard"
          title={_t("identification")}
          open={showDropdown.first}
          onToggle={() => handleDropdownToggle("first")}
        >
          <CategoryFlags
            llShowOnly={llShowOnly}
            hasOthersDocument={hasOthersDocument}
            isScannedDoc={isScannedDoc}
            hasWarning={hasWarning}
            hasHighRisk={hasHighRisk}
            tooltipIndex={1}
            _t={_t}
          />
          <ScannedTooltip index={1} />
          <WarningTooltip index={1} />
          <HighRiskTooltip index={1} />

          <CategoryCount value={`${uploadedDocs}/${totalDocs}`} _t={_t} />
        </CategoryHeader>
        {showDropdown.first && (
          <div className="details-docs__rows">
            {availableDocs.map((doc) => (
              <div
                key={doc.file_name}
                className={`details-docs__row ${getStatusRowClass(
                  getDocRowTone({
                    llShowOnly,
                    report: forensicDocumentReport?.[doc?.file_name],
                    forensicsEnabled: isForensicsEnabled,
                    isOtherUser: isOtherUserDoc(doc),
                  })
                )} ${
                  documentMatchesIdentifiers(
                    doc,
                    highlightedDocumentIdentifiers
                  )
                    ? "tenant-document-navigation-highlight"
                    : ""
                }`}
                data-tenant-document-identifiers={JSON.stringify(
                  getTenantDocumentIdentifiers(doc)
                )}
                style={{
                  opacity: !doc ? 0.4 : 1,
                  pointerEvents: !doc ? "none" : "",
                }}
              >
                <div className="details-docs__row-main">
                  {llShowOnly && (
                    <>
                      <DocStatusIcon
                        kind={getDocStatusKind(
                          forensicDocumentReport?.[doc?.file_name]
                        )}
                        tooltipId={`doc-first-dropdown-${doc?._id}`}
                      />
                      {(() => {
                        const tooltipId = `doc-first-dropdown-${doc?._id}`;
                        const customMessage = showKycCustomTooltipMessages(doc);
                        const report = forensicDocumentReport?.[doc?.file_name];
                        const langKey = currentLang === "en-us" ? "en" : "es";
                        const isScanned =
                          report?.documentType === "is_scanned_document" ||
                          report?.isScannedDocument === "yes";

                        const hasCustomMessage = haveValue(customMessage);
                        const hasReportData =
                          haveValue(report?.explanationSummary?.["en"]) ||
                          haveValue(report?.recommendedAction?.["en"]) ||
                          haveValue(report?.score);

                        if (hasCustomMessage) {
                          return (
                            <ReactTooltip
                              id={tooltipId}
                              noArrow
                              className="react-tooltip-styling details-skor-tooltip"
                              render={() => (
                                <div>
                                  <p>
                                    <span>{_t("explanationSummary")}: </span>
                                    {customMessage?.explanation}
                                  </p>
                                  <hr className="document-dropdown-tooltip-hr" />
                                  <p>
                                    <span>{_t("recommendedAction")}: </span>
                                    {customMessage?.recommended_action}
                                  </p>
                                </div>
                              )}
                            />
                          );
                        }

                        if (hasReportData) {
                          const explanation = isScanned
                            ? _t(
                                "thisDocumentIsAScanSoCouldNotRunForensicAnalysis"
                              )
                            : report?.explanationSummary?.[langKey];

                          const action = isScanned
                            ? _t(
                                "toEnableForensicChecksAskTheTenantToUploadTheOriginalDigitalPdf"
                              )
                            : report?.recommendedAction?.[langKey];

                          return (
                            <ReactTooltip
                              id={tooltipId}
                              noArrow
                              className="react-tooltip-styling details-skor-tooltip"
                              render={() => (
                                <div>
                                  {explanation && (
                                    <>
                                      <p>
                                        <span>
                                          {_t("explanationSummary")}:{" "}
                                        </span>
                                        {explanation}
                                      </p>
                                      <hr className="document-dropdown-tooltip-hr" />
                                    </>
                                  )}
                                  {action && (
                                    <p>
                                      <span>{_t("recommendedAction")}: </span>
                                      {action}
                                    </p>
                                  )}
                                  {!isScanned && report?.score && (
                                    <p>
                                      <span>
                                        {_t("confidenceInAuthenticity")}:{" "}
                                      </span>
                                      {`${report.score}%`}
                                    </p>
                                  )}
                                </div>
                              )}
                            />
                          );
                        }

                        return (
                          <ReactTooltip
                            id={tooltipId}
                            noArrow
                            className="react-tooltip-styling details-skor-tooltip"
                            render={() => (
                              <p>
                                <span>{_t("explanationSummary")}: </span>
                                {_t(
                                  "notAbleToRunForensicCheckForThisDocument",
                                  "Fall back text in document section tooltip when for a document forensic was not able to run.(Shown on hover)"
                                )}
                              </p>
                            )}
                          />
                        );
                      })()}
                    </>
                  )}

                  <div className="">
                    <p className="details-docs__name">
                      {getDisplayDocName(doc)}
                      <DocServiceTags tags={getDocServiceTags(doc)} />
                      {doc?.type === "Working life certificate" &&
                        haveValue(
                          forensicAnalysis?.["Work Life Certificate"]
                            ?.dataInJSON?.reportDate
                        ) && (
                          <>
                            {" "}
                            (
                            {moment(
                              forensicAnalysis?.["Work Life Certificate"]
                                ?.dataInJSON?.reportDate
                            ).format("D MMMM YYYY")}
                            )
                          </>
                        )}
                    </p>
                    {doc && (
                      <p className="details-docs__meta">
                        <span className="details-docs__date">
                          {_t("uploadedOn")}{" "}
                          {moment(doc.created_at).format("DD-MM-YYYY")}
                        </span>
                        {(isOtherUserDoc(doc) || doc?.signatory_name) && (
                          <DocOwnerBadge
                            doc={doc}
                            getDocOwnerLabel={getDocOwnerLabel}
                            _t={_t}
                          />
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <DocRowActions
                  doc={doc}
                  llShowOnly={llShowOnly}
                  readOnly={readOnly}
                  isOtherUserDoc={isOtherUserDoc}
                  setDeleteDocumentConfirmation={setDeleteDocumentConfirmation}
                  setselectedDocument={setselectedDocument}
                  handleViewDocument={handleViewDocument}
                  _t={_t}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// IncomeTaxesSection - Renders the "Income and Taxes" section
// Handles special logic for payslips which can have multiple instances
export function IncomeTaxesSection({
  allForensicDocuments,
  llShowOnly,
  readOnly = false,
  handleDropdownToggle,
  showDropdown,
  forensicDocumentReport,
  currentLang,
  isOtherUserDoc,
  getDocOwnerLabel,
  getDisplayDocName,
  setDeleteDocumentConfirmation,
  setselectedDocument,
  handleViewDocument,
  verdictPriority,
  forensicAnalysis,
  // Function-based props
  forensicDocsMap,
  employmentType,
  getDocsForType,
  getPayslipDocs,
  selectedVerdicts,
  matchesSelectedVerdicts,
  user,
  getDocServiceTags = () => [],
  highlightedDocumentIdentifiers = [],
  // Tenant explanation note (TenantMissingDocNote): canonical keys of the
  // explained documents plus the callback opening the explanation modal.
  // The callback is only passed on the tenant's own profile view.
  // onScrollToLandlordNote is the landlord counterpart: scrolling to the
  // read-only note box instead of opening the modal. Only passed on
  // landlord views.
  explainedDocKeys = [],
  onViewMissingDocNote = null,
  onScrollToLandlordNote = null,
}) {
  const isForensicsEnabled = useIsForensicsEnabled();
  const forensicRawData =
    user?.tenant_extra_field_id?.forensic_analysis?.profileReports ?? [];
  const allForensicDocumentData = (
    Array.isArray(forensicRawData) ? forensicRawData : []
  ).flatMap((entry) => {
    return (entry?.report?.documents_list || []).map((doc) => ({
      ...doc,
    }));
  });
  // #NOTE: allForensicDocuments has keys with the filenames and then inside the values there is data field,
  // inside that data field there is period_year, period_month.
  // also, these period_month and period_year are present inside the filename key's value.
  const { _t } = useTranslator();
  // Payslip sentinels carry the generic item "payslip" but each one stands
  // for one required period (e.g. "Payslip (September 2026)"). Resolve the
  // slot key ("1st month payslip", ...) from the period so a per-slot
  // explanation matches its own period row instead of none of them.
  // Slot N covers requiredPeriods[N - 1], same order as the sign-up step.
  const slotMatchingPeriods = getRequiredPayslipPeriods();
  const getPayslipSentinelSlotKey = (period) => {
    if (!period) return "payslip";
    const slotIndex = slotMatchingPeriods.findIndex(
      (required) =>
        required.period_year === period.period_year &&
        required.period_month === period.period_month
    );
    return slotIndex >= 0 ? PAYSLIP_ORDER[slotIndex] : "payslip";
  };
  const compulsoryDocs =
    forensicDocsMap?.[employmentType]?.income_taxes?.compulsory ?? [];
  const additionalDocs =
    forensicDocsMap?.[employmentType]?.income_taxes?.additional ?? [];
  const allDocs = [...compulsoryDocs, ...additionalDocs];

  if (allDocs.length === 0) return null;

  const categoryMatchedExtraDocs = [];
  const incomeRows = allDocs.flatMap((item) => {
    if (item?.toLowerCase()?.includes("payslip")) {
      const payslipDocs = getPayslipDocs();
      // #NOTE: payslipDocs is an array of object , in which each object has key "documentAnalysisData",
      // inside which there is period_month and period_year, or else,
      // there is data feild and inside data we have period_month and period_year.

      return payslipDocs.length
        ? payslipDocs.map((doc) => ({ item, doc }))
        : compulsoryDocs.includes(item)
          ? [{ item, doc: null, _isPayslipMissing: true }]
          : [];
    }

    const docs = getDocsForType(item, [
      DOCUMENT_CATEGORIES.TAX,
      DOCUMENT_CATEGORIES.INCOME,
      DOCUMENT_CATEGORIES.PAYMENT_RECEIPT,
      DOCUMENT_CATEGORIES.FINANCIAL,
    ]);
    const strictTypeDocs = docs.filter((doc) => doc?.type === item);
    const fallbackCategoryDocs = docs.filter((doc) => doc?.type !== item);
    const model100FallbackDocs =
      strictTypeDocs.length === 0 && item?.toLowerCase()?.includes("model 100")
        ? fallbackCategoryDocs.filter(
            // A company's tax docs are not the tenant's own Model 100.
            (doc) =>
              doc?.categoryKey === DOCUMENT_CATEGORIES.TAX &&
              doc?.analysisOwnerType !== "legal_entity" &&
              doc?.documentAnalysisData?.ownerType !== "legal_entity"
          )
        : [];

    if (fallbackCategoryDocs.length > 0) {
      categoryMatchedExtraDocs.push(
        ...fallbackCategoryDocs.filter(
          (doc) => !model100FallbackDocs.includes(doc)
        )
      );
    }

    return strictTypeDocs.length
      ? strictTypeDocs.map((doc) => ({ item, doc }))
      : model100FallbackDocs.length
        ? model100FallbackDocs.map((doc) => ({ item, doc }))
        : compulsoryDocs.includes(item)
          ? [{ item, doc: null }]
          : [];
  });
  const finalIncomeRows = dedupeDocRows([
    ...incomeRows,
    ...dedupeDocs(categoryMatchedExtraDocs).map((doc) => ({
      item: doc?.type ?? "",
      doc,
    })),
  ]);

  const filteredDocs =
    selectedVerdicts.length === 0
      ? finalIncomeRows
      : finalIncomeRows.filter(
          ({ doc }) => doc && matchesSelectedVerdicts(doc)
        );

  if (selectedVerdicts.length > 0 && filteredDocs.length === 0) return null;

  // Detect verdicts present in this section
  let hasHighRisk = false;
  let hasWarning = false;
  let hasOthersDocument = false;
  let isScannedDoc = false;
  let hasPayslipDateWarning = false;

  for (const { doc } of filteredDocs) {
    const fileName = doc?.file_name;
    const verdict = forensicDocumentReport?.[fileName]?.verdict;
    const isScanned =
      forensicDocumentReport?.[fileName]?.documentType ===
        "is_scanned_document" ||
      forensicDocumentReport?.[fileName]?.isScannedDocument === "yes";
    if (isScanned) {
      isScannedDoc = true;
      continue;
    }
    if (verdict === "HIGH_RISK") hasHighRisk = true;
    if (verdict === "WARNING") hasWarning = true;
  }

  // Check payslip date warning for employed and public servant users
  const isThreePayslipRequired =
    user?.employment_status === constants.employed ||
    user?.employment_status === constants.public_servant;

  let requiredPayslipPeriods = [];
  let payslipDocWarnings = new Map();

  if (isThreePayslipRequired) {
    requiredPayslipPeriods = getRequiredPayslipPeriods();
    const payslipDocs = getPayslipDocs();

    for (const doc of payslipDocs) {
      const forensicData = allForensicDocuments?.[doc?.file_name];
      const payslipPeriodYear = forensicData?.data?.period_year;
      const payslipPeriodMonth = forensicData?.data?.period_month;

      if (!payslipPeriodYear || !payslipPeriodMonth) {
        continue;
      }

      const payslipObj = {
        period_year: payslipPeriodYear,
        period_month: payslipPeriodMonth,
        date: doc?.date,
        created_at: doc?.created_at,
      };

      const warningMessage = getPayslipDateWarning(
        payslipObj,
        requiredPayslipPeriods,
        _t
      );

      if (warningMessage) {
        payslipDocWarnings.set(doc?._id || doc?.file_name, warningMessage);
        hasPayslipDateWarning = true;
      }
    }
  }

  for (const { doc } of filteredDocs) {
    if (isOtherUserDoc(doc)) {
      hasOthersDocument = true;
      break;
    }
  }

  const getPriority = (entry) => {
    if (entry._isPayslipMissing) return -1;
    const doc = entry.doc;
    if (!doc) return -1;
    const isScanned =
      forensicDocumentReport?.[doc?.file_name]?.documentType ===
        "is_scanned_document" ||
      forensicDocumentReport?.[doc?.file_name]?.isScannedDocument === "yes";
    if (isScanned) return verdictPriority["SCANNED"];
    const verdict = forensicDocumentReport?.[doc?.file_name]?.verdict;
    if (!verdict) return verdictPriority["WARNING"];
    return verdictPriority[verdict] ?? 3;
  };

  const sortedDocs = [...filteredDocs].sort(
    (a, b) => getPriority(a) - getPriority(b)
  );

  // Rebuild payslip slots — always exactly 3 for required periods, plus any old docs
  const nonPayslipSorted = sortedDocs.filter(
    (entry) => !entry.item?.toLowerCase()?.includes("payslip")
  );

  let payslipSlots = [];
  if (isThreePayslipRequired && requiredPayslipPeriods.length > 0) {
    const allPayslipDocs =
      selectedVerdicts.length > 0
        ? getPayslipDocs().filter((doc) => matchesSelectedVerdicts(doc))
        : getPayslipDocs();
    const usedDocKeys = new Set();

    const requiredSlots = requiredPayslipPeriods.map((period) => {
      const matchingDoc = allPayslipDocs.find((doc) => {
        const key = getUniqueDocKey(doc);
        if (usedDocKeys.has(key)) return false;
        // const forensicData = allForensicDocuments?.[doc?.file_name];
        const forensicData = allForensicDocumentData?.find(
          (entry) => entry?.instanceId === doc?.documentAnalysisData?.instanceId
        );
        const payslipObj = {
          period_year: Number(
            forensicData?.data?.period_year ??
              doc?.documentAnalysisData?.data?.period_year ??
              doc?.documentAnalysisData?.period_year
          ),
          period_month: Number(
            forensicData?.data?.period_month ??
              doc?.documentAnalysisData?.data?.period_month ??
              doc?.documentAnalysisData?.period_month
          ),
          date: doc?.date,
          created_at: doc?.created_at,
        };
        const matched = isPayslipWithinRequiredPeriod(payslipObj, [period]);
        if (matched) usedDocKeys.add(key);
        return matched;
      });

      return matchingDoc
        ? { item: "payslip", doc: matchingDoc }
        : {
            item: "payslip",
            doc: null,
            _isPayslipMissing: true,
            _payslipPeriod: period,
          };
    });

    const oldPayslipDocs = allPayslipDocs.filter(
      (doc) => !usedDocKeys.has(getUniqueDocKey(doc))
    );

    payslipSlots = [
      ...requiredSlots,
      ...oldPayslipDocs.map((doc) => ({ item: "payslip", doc })),
    ];
  } else {
    const existingDocCount = sortedDocs.filter(
      (entry) => entry.item?.toLowerCase()?.includes("payslip") && entry.doc
    ).length;
    if (isThreePayslipRequired) {
      payslipSlots = [
        ...sortedDocs.filter(
          (entry) => entry.item?.toLowerCase()?.includes("payslip") && entry.doc
        ),
        ...Array.from({ length: Math.max(0, 3 - existingDocCount) }, () => ({
          item: "payslip",
          doc: null,
          _isPayslipMissing: true,
        })),
      ];
    } else {
      payslipSlots = sortedDocs.filter((entry) =>
        entry.item?.toLowerCase()?.includes("payslip")
      );
    }
  }

  payslipSlots?.sort((a, b) => getPriority(a) - getPriority(b));

  const hasMissingPayslipPeriods = payslipSlots.some(
    (entry) => entry._isPayslipMissing
  );

  const displayDocs = [...nonPayslipSorted, ...payslipSlots].sort(
    (a, b) => getPriority(a) - getPriority(b)
  );

  const totalDocs = displayDocs.length;
  const uploadedDocs = displayDocs.filter(({ doc }) => !!doc).length;
  const hasMissingDocs = uploadedDocs < totalDocs;

  // Landlord header indicator: number of rows in this section showing the
  // read-only "View note" pill (display only, not clickable). Mirrors the
  // per-row pill conditions below (missing rows only, never uploaded rows).
  const incomeNotePillCount =
    typeof onScrollToLandlordNote === "function"
      ? displayDocs.filter((entry) => {
          if (entry._isPayslipMissing) {
            return isMissingDocKeyExplained(
              explainedDocKeys,
              getPayslipSentinelSlotKey(entry._payslipPeriod)
            );
          }
          if (!entry.doc) {
            return isMissingDocKeyExplained(explainedDocKeys, entry.item);
          }
          return false;
        }).length
      : 0;

  return (
    <div className="details-docs__cat">
      <CategoryHeader
        icon="wallet"
        title={_t("incomeAndTaxes")}
        open={showDropdown.third}
        onToggle={() => handleDropdownToggle("third")}
      >
        <CategoryFlags
          llShowOnly={llShowOnly}
          hasOthersDocument={hasOthersDocument}
          isScannedDoc={isScannedDoc}
          hasWarning={hasWarning}
          hasHighRisk={hasHighRisk}
          hasMissing={displayDocs.some(
            (entry) =>
              !entry.doc &&
              !isMissingDocKeyExplained(
                explainedDocKeys,
                entry._isPayslipMissing
                  ? getPayslipSentinelSlotKey(entry._payslipPeriod)
                  : entry.item
              )
          )}
          tooltipIndex={3}
          missingTooltipIndex={2}
          _t={_t}
        />
        <ScannedTooltip index={3} />
        <MissingDocTooltip index={2} />
        <WarningTooltip index={3} />
        <HighRiskTooltip index={3} />

        <CategoryNoteCount count={incomeNotePillCount} />
        <CategoryCount value={`${uploadedDocs}/${totalDocs}`} _t={_t} />
      </CategoryHeader>

      {showDropdown.third && (
        <div className="details-docs__rows">
          {displayDocs.map((entry, idx) => {
            // Missing payslip sentinel render
            if (entry._isPayslipMissing) {
              // Explained payslips stay missing but show a "View note"
              // button instead of the missing warning. The sentinel item is
              // generic ("payslip"), so resolve this row's slot key from its
              // period for a per-slot match.
              const sentinelShowsNoteButton =
                typeof onViewMissingDocNote === "function" &&
                isMissingDocKeyExplained(
                  explainedDocKeys,
                  getPayslipSentinelSlotKey(entry._payslipPeriod)
                );
              // Landlord: the same explained row shows a note pill scrolling
              // to the read-only note box.
              const sentinelSlotKey = getPayslipSentinelSlotKey(
                entry._payslipPeriod
              );
              const sentinelShowsNotePill =
                typeof onScrollToLandlordNote === "function" &&
                isMissingDocKeyExplained(explainedDocKeys, sentinelSlotKey);
              const periodLabel = entry._payslipPeriod
                ? `${entry._payslipPeriod.monthName} ${entry._payslipPeriod.period_year}`
                : "";
              return (
                <div
                  key={`payslip-missing-${entry._payslipPeriod?.period_year ?? ""}-${entry._payslipPeriod?.period_month ?? idx}`}
                  id={
                    sentinelShowsNotePill
                      ? getMissingDocRowAnchorId(sentinelSlotKey)
                      : undefined
                  }
                  className="details-docs__row"
                  style={{
                    opacity:
                      sentinelShowsNoteButton || sentinelShowsNotePill
                        ? 1
                        : 0.4,
                  }}
                >
                  <div className="details-docs__row-main">
                    {llShowOnly && <DocStatusIcon kind="missing" />}
                    <div className="">
                      <div className="details-docs__name">
                        <p>
                          {periodLabel
                            ? `${_t("payslip")} (${periodLabel})`
                            : _t("payslip")}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div
                    className="details-docs__row-note"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--space-8)",
                    }}
                  >
                    {sentinelShowsNoteButton ? (
                      <ViewMissingDocNoteButton
                        onViewNote={onViewMissingDocNote}
                      />
                    ) : (
                      <>
                        {sentinelShowsNotePill && (
                          <LandlordNotePillButton
                            onScrollToNote={onScrollToLandlordNote}
                          />
                        )}
                        {_t("documentMissing")}
                      </>
                    )}
                  </div>
                </div>
              );
            }

            const doc = entry.doc;
            const item = entry.item;
            // Explained documents keep their missing state, but the tenant
            // sees a "View note" button instead of the missing warning.
            const incomeRowShowsNoteButton =
              !doc &&
              typeof onViewMissingDocNote === "function" &&
              isMissingDocKeyExplained(explainedDocKeys, item);
            // A document uploaded for another person does not count for this
            // tenant, so an explained key still earns a "View note" button
            // on its row. A matched third-party payslip fills a required
            // slot with the generic item "payslip", so resolve its slot from
            // the document's own period for a per-slot match (same order
            // and matcher as the slot assignment above).
            const getPayslipDocSlotKey = (payslipDoc) => {
              const forensicData = allForensicDocumentData?.find(
                (forensicEntry) =>
                  forensicEntry?.instanceId ===
                  payslipDoc?.documentAnalysisData?.instanceId
              );
              const payslipObj = {
                period_year: Number(
                  forensicData?.data?.period_year ??
                    payslipDoc?.documentAnalysisData?.data?.period_year ??
                    payslipDoc?.documentAnalysisData?.period_year
                ),
                period_month: Number(
                  forensicData?.data?.period_month ??
                    payslipDoc?.documentAnalysisData?.data?.period_month ??
                    payslipDoc?.documentAnalysisData?.period_month
                ),
                date: payslipDoc?.date,
                created_at: payslipDoc?.created_at,
              };
              const slotIndex = slotMatchingPeriods.findIndex((period) =>
                isPayslipWithinRequiredPeriod(payslipObj, [period])
              );
              return slotIndex >= 0 ? PAYSLIP_ORDER[slotIndex] : null;
            };
            const incomeThirdPartySlotKey =
              doc && doc?.type?.toLowerCase()?.includes("payslip")
                ? getPayslipDocSlotKey(doc)
                : null;
            const incomeThirdPartyShowsNoteButton =
              doc &&
              isOtherUserDoc(doc) &&
              typeof onViewMissingDocNote === "function" &&
              (isMissingDocKeyExplained(explainedDocKeys, item) ||
                (incomeThirdPartySlotKey &&
                  isMissingDocKeyExplained(
                    explainedDocKeys,
                    incomeThirdPartySlotKey
                  )));
            // Landlord: explained missing rows show a note pill scrolling to
            // the read-only note box. Anchor on the key that matched so
            // the box's document links land back on this row. Only when
            // the document is actually missing — never on uploaded rows,
            // including third-party ones.
            const incomeNotePillKey = !doc ? item : null;
            const incomeShowsNotePill =
              typeof onScrollToLandlordNote === "function" &&
              incomeNotePillKey &&
              isMissingDocKeyExplained(explainedDocKeys, incomeNotePillKey);

            const tooltipId = `doc-third-dropdown-${doc?._id}`;
            const report = forensicDocumentReport?.[doc?.file_name];
            const lang = currentLang === "en-us" ? "en" : "es";
            const isScanned =
              report?.documentType === "is_scanned_document" ||
              report?.isScannedDocument === "yes";

            const hasReportData =
              haveValue(report?.explanationSummary?.["en"]) ||
              haveValue(report?.recommendedAction?.["en"]) ||
              haveValue(report?.score);

            const getStatusKind = () =>
              doc ? getDocStatusKind(report) : "missing";

            return (
              <div
                key={doc?._id || item || idx}
                id={
                  incomeShowsNotePill
                    ? getMissingDocRowAnchorId(incomeNotePillKey)
                    : undefined
                }
                className={`details-docs__row ${getStatusRowClass(
                  getDocRowTone({
                    llShowOnly,
                    report: report,
                    forensicsEnabled: isForensicsEnabled,
                    isOtherUser: isOtherUserDoc(doc),
                  })
                )} ${
                  documentMatchesIdentifiers(
                    doc,
                    highlightedDocumentIdentifiers
                  )
                    ? "tenant-document-navigation-highlight"
                    : ""
                }`}
                data-tenant-document-identifiers={JSON.stringify(
                  getTenantDocumentIdentifiers(doc)
                )}
                style={{
                  opacity:
                    !doc && !incomeRowShowsNoteButton && !incomeShowsNotePill
                      ? 0.4
                      : 1,
                  pointerEvents:
                    !doc && !incomeRowShowsNoteButton && !incomeShowsNotePill
                      ? "none"
                      : "",
                }}
              >
                <div className="details-docs__row-main">
                  {llShowOnly && (
                    <>
                      <DocStatusIcon
                        kind={getStatusKind()}
                        tooltipId={tooltipId}
                      />
                      {hasReportData ? (
                        <ReactTooltip
                          id={tooltipId}
                          noArrow
                          className="react-tooltip-styling details-skor-tooltip"
                          render={() => (
                            <div>
                              {report?.explanationSummary?.[lang] && (
                                <>
                                  <p>
                                    <span>{_t("explanationSummary")}: </span>
                                    {isScanned
                                      ? _t(
                                          "thisDocumentIsAScanSoCouldNotRunForensicAnalysis"
                                        )
                                      : report?.explanationSummary?.[lang]}
                                  </p>
                                  <hr className="document-dropdown-tooltip-hr" />
                                </>
                              )}
                              {report?.recommendedAction?.[lang] && (
                                <p>
                                  <span>{_t("recommendedAction")}: </span>
                                  {isScanned
                                    ? _t(
                                        "toEnableForensicChecksAskTheTenantToUploadTheOriginalDigitalPdf"
                                      )
                                    : report?.recommendedAction?.[lang]}
                                </p>
                              )}
                              {!isScanned && report?.score && (
                                <p>
                                  <span>
                                    {_t("confidenceInAuthenticity")}:{" "}
                                  </span>
                                  {`${report.score}%`}
                                </p>
                              )}
                            </div>
                          )}
                        />
                      ) : (
                        <ReactTooltip
                          id={tooltipId}
                          noArrow
                          className="react-tooltip-styling details-skor-tooltip"
                          render={() => (
                            <p>
                              <span>{_t("explanationSummary")}: </span>
                              {_t(
                                "notAbleToRunForensicCheckForThisDocument",
                                "Fall back text in document section tooltip when for a document forensic was not able to run.(Shown on hover)"
                              )}
                            </p>
                          )}
                        />
                      )}
                    </>
                  )}
                  <div className="">
                    <div className="details-docs__name details-docs__name--tags">
                      <p className="">
                        {getDisplayDocName(doc)}
                        {doc?.type === "Working life certificate" &&
                          haveValue(
                            forensicAnalysis?.["Work Life Certificate"]
                              ?.dataInJSON?.reportDate
                          ) && (
                            <>
                              {" "}
                              (
                              {moment(
                                forensicAnalysis?.["Work Life Certificate"]
                                  ?.dataInJSON?.reportDate
                              ).format("D MMMM YYYY")}
                              )
                            </>
                          )}
                        {!doc && `${_t(item, "", "fixed")}`}
                      </p>
                      <DocServiceTags tags={getDocServiceTags(doc)} />
                      {/* {isThreePayslipRequired &&
                        doc?.type?.toLowerCase()?.includes("payslip") &&
                        payslipDocWarnings.has(doc?._id || doc?.file_name) && (
                          <p className="doc-warning-message d-flex align-items-center">
                            <img
                              src="/images/warning.svg"
                              alt=""
                              className="me-1 mt-1"
                              style={{ width: "0.875rem", height: "0.875rem" }}
                            />
                            <span>
                              {payslipDocWarnings.get(
                                doc?._id || doc?.file_name
                              )}
                            </span>
                          </p>
                        )
                        } */}
                    </div>
                    {doc && (
                      <p className="details-docs__meta">
                        <span className="details-docs__date">
                          {_t("uploadedOn")}{" "}
                          {moment(doc?.created_at).format("DD-MM-YYYY")}
                        </span>
                        {(isOtherUserDoc(doc) || doc?.signatory_name) && (
                          <DocOwnerBadge
                            doc={doc}
                            getDocOwnerLabel={getDocOwnerLabel}
                            _t={_t}
                          />
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <DocRowActions
                  doc={doc}
                  llShowOnly={llShowOnly}
                  readOnly={readOnly}
                  isOtherUserDoc={isOtherUserDoc}
                  setDeleteDocumentConfirmation={setDeleteDocumentConfirmation}
                  setselectedDocument={setselectedDocument}
                  handleViewDocument={handleViewDocument}
                  showMissingText={!incomeRowShowsNoteButton}
                  _t={_t}
                />
                {(incomeRowShowsNoteButton ||
                  incomeThirdPartyShowsNoteButton) && (
                  <ViewMissingDocNoteButton onViewNote={onViewMissingDocNote} />
                )}
                {incomeShowsNotePill && (
                  <LandlordNotePillButton
                    onScrollToNote={onScrollToLandlordNote}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// EmploymentStatusSection - Renders the "Employment Status" section
export function EmploymentStatusSection({
  llShowOnly,
  readOnly = false,
  handleDropdownToggle,
  showDropdown,
  forensicDocumentReport,
  currentLang,
  isOtherUserDoc,
  getDocOwnerLabel,
  getDisplayDocName,
  setDeleteDocumentConfirmation,
  setselectedDocument,
  handleViewDocument,
  verdictPriority,
  forensicAnalysis,
  // Function-based props
  forensicDocsMap,
  employmentType,
  getDocsForType,
  selectedVerdicts,
  matchesSelectedVerdicts,
  getDocServiceTags = () => [],
  highlightedDocumentIdentifiers = [],
  // Tenant explanation note (TenantMissingDocNote): canonical keys of the
  // explained documents plus the callback opening the explanation modal.
  // The callback is only passed on the tenant's own profile view.
  // onScrollToLandlordNote is the landlord counterpart: scrolling to the
  // read-only note box instead of opening the modal. Only passed on
  // landlord views.
  explainedDocKeys = [],
  onViewMissingDocNote = null,
  onScrollToLandlordNote = null,
  // Optional: render another group of the docs map with the same layout
  // (company login tenants use company_identity / legal_representative /
  // income_taxes). Defaults keep the Employment status section as it was.
  groupKey = "employment_status",
  title,
  icon = "briefcase",
  dropdownKey = "sec",
  categoryKeys = [DOCUMENT_CATEGORIES.EMPLOYMENT_STATUS],
}) {
  const isForensicsEnabled = useIsForensicsEnabled();
  const { _t } = useTranslator();
  const compulsoryDocs =
    forensicDocsMap?.[employmentType]?.[groupKey]?.compulsory ?? [];
  const additionalDocs =
    forensicDocsMap?.[employmentType]?.[groupKey]?.additional ?? [];
  const allDocs = [...compulsoryDocs, ...additionalDocs];

  if (allDocs.length === 0) return null;

  const consumedDocKeys = new Set();
  const getDocKey = (doc) => getUniqueDocKey(doc);

  const employmentRows = allDocs.flatMap((item) => {
    const isCompulsory = compulsoryDocs.includes(item);
    const docs = dedupeDocs(getDocsForType(item, categoryKeys));
    const strictTypeDocs = docs.filter((doc) => doc?.type === item);
    // const fallbackCategoryDocs = docs.filter((doc) => doc?.type !== item);

    const availableStrict = strictTypeDocs.filter(
      (doc) => !consumedDocKeys.has(getDocKey(doc))
    );
    if (availableStrict.length > 0) {
      availableStrict.forEach((doc) => consumedDocKeys.add(getDocKey(doc)));
      return availableStrict.map((doc) => ({ item, doc }));
    }

    // If no strict type match, show missing only for compulsory docs
    if (isCompulsory) {
      return [{ item, doc: null }];
    }

    return [];
  });

  // Append remaining employment-category docs not used to satisfy required slots.
  const allEmploymentCategoryDocs = dedupeDocs(
    allDocs.flatMap((item) => getDocsForType(item, categoryKeys))
  );
  const extraEmploymentDocs = allEmploymentCategoryDocs.filter(
    (doc) => !consumedDocKeys.has(getDocKey(doc))
  );
  const finalEmploymentRows = dedupeDocRows([
    ...employmentRows,
    ...extraEmploymentDocs.map((doc) => ({ item: doc?.type ?? "", doc })),
  ]);

  const filteredDocs =
    selectedVerdicts.length === 0
      ? finalEmploymentRows
      : finalEmploymentRows.filter(
          ({ doc }) => doc && matchesSelectedVerdicts(doc)
        );

  if (selectedVerdicts.length > 0 && filteredDocs.length === 0) return null;

  const totalDocs = filteredDocs.length;
  const uploadedDocs = filteredDocs.filter(({ doc }) => !!doc).length;

  let hasHighRisk = false;
  let hasWarning = false;
  let hasOthersDocument = false;
  let isScannedDoc = false;

  for (const { doc } of filteredDocs) {
    const fileName = doc?.file_name;
    const verdict = forensicDocumentReport?.[fileName]?.verdict;

    const isScanned =
      forensicDocumentReport?.[fileName]?.documentType ===
        "is_scanned_document" ||
      forensicDocumentReport?.[fileName]?.isScannedDocument === "yes";
    if (isScanned) {
      isScannedDoc = true;
      continue;
    }
    if (verdict === "HIGH_RISK") hasHighRisk = true;
    if (verdict === "WARNING") hasWarning = true;
  }

  const hasMissingDocs = uploadedDocs < totalDocs;

  for (const { doc } of filteredDocs) {
    if (isOtherUserDoc(doc)) {
      hasOthersDocument = true;
      break;
    }
  }

  const getPriority = (doc) => {
    if (!doc) return -1;
    const isScanned =
      forensicDocumentReport?.[doc?.file_name]?.documentType ===
        "is_scanned_document" ||
      forensicDocumentReport?.[doc?.file_name]?.isScannedDocument === "yes";
    if (isScanned) return verdictPriority["SCANNED"];
    const verdict = forensicDocumentReport?.[doc?.file_name]?.verdict;
    if (!verdict) return verdictPriority["WARNING"];
    return verdictPriority[verdict] ?? 3;
  };

  const sortedDocs = [...filteredDocs].sort((a, b) => {
    const docA = a?.doc;
    const docB = b?.doc;
    return getPriority(docA) - getPriority(docB);
  });

  // Landlord header indicator: number of rows in this section showing the
  // read-only "View note" pill (display only, not clickable).
  const employmentNotePillCount =
    typeof onScrollToLandlordNote === "function"
      ? filteredDocs.filter(
          ({ item, doc }) =>
            !doc && isMissingDocKeyExplained(explainedDocKeys, item)
        ).length
      : 0;

  return (
    <div className="details-docs__cat">
      <CategoryHeader
        icon={icon}
        title={title ?? _t("employmentStatus")}
        open={showDropdown[dropdownKey]}
        onToggle={() => handleDropdownToggle(dropdownKey)}
      >
        <CategoryFlags
          llShowOnly={llShowOnly}
          hasOthersDocument={hasOthersDocument}
          isScannedDoc={isScannedDoc}
          hasWarning={hasWarning}
          hasHighRisk={hasHighRisk}
          hasMissing={filteredDocs.some(
            ({ item, doc }) =>
              !doc && !isMissingDocKeyExplained(explainedDocKeys, item)
          )}
          tooltipIndex={2}
          missingTooltipIndex={1}
          _t={_t}
        />
        <ScannedTooltip index={2} />
        <MissingDocTooltip index={1} />
        <WarningTooltip index={2} />
        <HighRiskTooltip index={2} />

        <CategoryNoteCount count={employmentNotePillCount} />
        <CategoryCount value={`${uploadedDocs}/${totalDocs}`} _t={_t} />
      </CategoryHeader>

      {showDropdown[dropdownKey] && (
        <div className="details-docs__rows">
          {sortedDocs.map(({ item, doc }) => {
            const title = doc ? getDisplayDocName(doc) : _t(item, "", "fixed");
            // Explained documents keep their missing state, but the tenant
            // sees a "View note" button instead of the missing warning.
            const employmentRowShowsNoteButton =
              !doc &&
              typeof onViewMissingDocNote === "function" &&
              isMissingDocKeyExplained(explainedDocKeys, item);
            // A document uploaded for another person does not count for this
            // tenant, so an explained key still earns a "View note" button
            // on its row (next to the third-party badge).
            const employmentThirdPartyShowsNoteButton =
              doc &&
              isOtherUserDoc(doc) &&
              typeof onViewMissingDocNote === "function" &&
              isMissingDocKeyExplained(explainedDocKeys, item);
            // Landlord: explained missing rows show a note pill scrolling to
            // the read-only note box. Only when the document is actually
            // missing — never on uploaded rows, including third-party ones.
            const employmentShowsNotePill =
              !doc &&
              typeof onScrollToLandlordNote === "function" &&
              isMissingDocKeyExplained(explainedDocKeys, item);

            return (
              <div
                key={doc?.file_name ?? `${item}-missing`}
                id={
                  employmentShowsNotePill
                    ? getMissingDocRowAnchorId(item)
                    : undefined
                }
                className={`details-docs__row ${getStatusRowClass(
                  getDocRowTone({
                    llShowOnly,
                    report: forensicDocumentReport?.[doc?.file_name],
                    forensicsEnabled: isForensicsEnabled,
                    isOtherUser: isOtherUserDoc(doc),
                  })
                )} ${
                  documentMatchesIdentifiers(
                    doc,
                    highlightedDocumentIdentifiers
                  )
                    ? "tenant-document-navigation-highlight"
                    : ""
                }`}
                data-tenant-document-identifiers={JSON.stringify(
                  getTenantDocumentIdentifiers(doc)
                )}
                style={{
                  opacity:
                    !doc &&
                    !employmentRowShowsNoteButton &&
                    !employmentShowsNotePill
                      ? 0.4
                      : 1,
                  pointerEvents:
                    !doc &&
                    !employmentRowShowsNoteButton &&
                    !employmentShowsNotePill
                      ? "none"
                      : "",
                }}
              >
                <div className="details-docs__row-main">
                  {llShowOnly && (
                    <>
                      <DocStatusIcon
                        kind={
                          doc
                            ? getDocStatusKind(
                                forensicDocumentReport?.[doc?.file_name]
                              )
                            : "missing"
                        }
                        tooltipId={`doc-second-dropdown-${doc?._id}`}
                      />
                      {haveValue(
                        forensicDocumentReport?.[doc?.file_name]
                          ?.explanationSummary?.["en"]
                      ) ||
                      haveValue(
                        forensicDocumentReport?.[doc?.file_name]
                          ?.recommendedAction?.["en"]
                      ) ||
                      haveValue(
                        forensicDocumentReport?.[doc?.file_name]?.score
                      ) ? (
                        <ReactTooltip
                          id={`doc-second-dropdown-${doc?._id}`}
                          noArrow
                          className="react-tooltip-styling details-skor-tooltip"
                          render={() => (
                            <div>
                              {forensicDocumentReport?.[doc?.file_name]
                                ?.explanationSummary?.[
                                currentLang === "en-us" ? "en" : "es"
                              ] && (
                                <>
                                  <p>
                                    <span>{_t("explanationSummary")}: </span>
                                    {forensicDocumentReport?.[doc?.file_name]
                                      ?.documentType ===
                                      "is_scanned_document" ||
                                    forensicDocumentReport?.[doc?.file_name]
                                      ?.isScannedDocument === "yes"
                                      ? _t(
                                          "thisDocumentIsAScanSoCouldNotRunForensicAnalysis"
                                        )
                                      : forensicDocumentReport?.[doc?.file_name]
                                          ?.explanationSummary?.[
                                          currentLang === "en-us" ? "en" : "es"
                                        ]}
                                  </p>
                                  <hr className="document-dropdown-tooltip-hr" />
                                </>
                              )}
                              {forensicDocumentReport?.[doc?.file_name]
                                ?.recommendedAction?.[
                                currentLang === "en-us" ? "en" : "es"
                              ] && (
                                <p>
                                  <span>{_t("recommendedAction")}: </span>
                                  {forensicDocumentReport?.[doc?.file_name]
                                    ?.documentType === "is_scanned_document" ||
                                  forensicDocumentReport?.[doc?.file_name]
                                    ?.isScannedDocument === "yes"
                                    ? _t(
                                        "toEnableForensicChecksAskTheTenantToUploadTheOriginalDigitalPdf"
                                      )
                                    : forensicDocumentReport?.[doc?.file_name]
                                        ?.recommendedAction?.[
                                        currentLang === "en-us" ? "en" : "es"
                                      ]}
                                </p>
                              )}
                              {!(
                                forensicDocumentReport?.[doc?.file_name]
                                  ?.documentType === "is_scanned_document" ||
                                forensicDocumentReport?.[doc?.file_name]
                                  ?.isScannedDocument === "yes"
                              ) &&
                                forensicDocumentReport?.[doc?.file_name]
                                  ?.score && (
                                  <p>
                                    <span>
                                      {_t("confidenceInAuthenticity")}:{" "}
                                    </span>
                                    {`${forensicDocumentReport?.[doc?.file_name]?.score}%`}
                                  </p>
                                )}
                            </div>
                          )}
                        />
                      ) : (
                        <ReactTooltip
                          id={`doc-second-dropdown-${doc?._id}`}
                          noArrow
                          className="react-tooltip-styling details-skor-tooltip"
                          render={() => (
                            <p>
                              <span>{_t("explanationSummary")}: </span>
                              {_t(
                                "notAbleToRunForensicCheckForThisDocument",
                                "Fall back text in document section tooltip when for a document forensic was not able to run.(Shown on hover)"
                              )}
                            </p>
                          )}
                        />
                      )}
                    </>
                  )}
                  <div>
                    <p className="details-docs__name">
                      {getDisplayDocName(doc)}
                      <DocServiceTags tags={getDocServiceTags(doc)} />
                      {doc?.type === "Working life certificate" &&
                        haveValue(
                          forensicAnalysis?.["Work Life Certificate"]
                            ?.dataInJSON?.reportDate
                        ) && (
                          <>
                            {" "}
                            (
                            {moment(
                              forensicAnalysis?.["Work Life Certificate"]
                                ?.dataInJSON?.reportDate
                            ).format("D MMMM YYYY")}
                            )
                          </>
                        )}
                      {!doc && `${_t(item, "", "fixed")}`}
                    </p>
                    {doc && (
                      <p className="details-docs__meta">
                        <span className="details-docs__date">
                          {_t("uploadedOn")}{" "}
                          {moment(doc?.created_at).format("DD-MM-YYYY")}
                        </span>
                        {(isOtherUserDoc(doc) || doc?.signatory_name) && (
                          <DocOwnerBadge
                            doc={doc}
                            getDocOwnerLabel={getDocOwnerLabel}
                            _t={_t}
                          />
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <DocRowActions
                  doc={doc}
                  llShowOnly={llShowOnly}
                  readOnly={readOnly}
                  isOtherUserDoc={isOtherUserDoc}
                  setDeleteDocumentConfirmation={setDeleteDocumentConfirmation}
                  setselectedDocument={setselectedDocument}
                  handleViewDocument={handleViewDocument}
                  showMissingText={
                    !employmentRowShowsNoteButton &&
                    !employmentThirdPartyShowsNoteButton
                  }
                  _t={_t}
                />
                {(employmentRowShowsNoteButton ||
                  employmentThirdPartyShowsNoteButton) && (
                  <ViewMissingDocNoteButton onViewNote={onViewMissingDocNote} />
                )}
                {employmentShowsNotePill && (
                  <LandlordNotePillButton
                    onScrollToNote={onScrollToLandlordNote}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
